import { QuestionCircleOutlined } from '@ant-design/icons';
import { Flex, Table, Tag, Tooltip } from 'antd';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import FormattedInputNumber from '@/components/general/FormattedInputNumber';
import { Package } from '@/model/Logistic/Package';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';

interface ServiceOrderShippedItemsTableProps {
  packageGroup: {
    packageId?: number | undefined;
    pkg?: Package | undefined;
    items: PackageItem[];
  };
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
  serviceOrderItems: ServiceOrderItem[];
  invalidDistributionSet: Set<number>;
}

const isNoReturnOrSaleReference = (reference?: SHIPPING_ITEM_REFERENCE) =>
  reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO ||
  reference === SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION;
const ServiceOrderShippedItemsTable = (props: ServiceOrderShippedItemsTableProps) => {
  const { t } = useTranslation();
  const { packageGroup, serviceOrderItems, setServiceOrderItems, invalidDistributionSet } = props;
  const getRowState = (item: PackageItem) => {
    const id = item.id;
    const maxAmount = Math.max(item.execute_amount ?? 0, 0);
    const selected = id
      ? serviceOrderItems.find(soItem => {
          const packageItemId =
            typeof soItem.package_item === 'number' ? soItem.package_item : soItem.package_item?.id;

          return packageItemId === id;
        })
      : undefined;
    const stockAmount = selected?.stock_technician_amount ?? 0;
    const discardedAmount = selected?.discarded_amount ?? 0;
    const toReturnAmount = selected?.to_return_amount ?? 0;

    return {
      maxAmount,
      maxStockAllowed: Math.max(maxAmount - discardedAmount - toReturnAmount, 0),
      maxDiscardedAllowed: Math.max(maxAmount - stockAmount - toReturnAmount, 0),
      maxReturnAllowed: Math.max(maxAmount - stockAmount - discardedAmount, 0),
      selected,
      isInvalid: typeof id === 'number' && invalidDistributionSet.has(id),
      isNoReturnOrSale: isNoReturnOrSaleReference(item.item_shipped_reference),
    };
  };

  // Centralizacion de la actualizacion del item vinculado al package_item para evitar repeticion.
  const updateServiceOrderItem = useCallback(
    (packageItemId: number, updater: (item: ServiceOrderItem) => void) => {
      // Usar prevValue para encontrar el item real y evitar sobrescribir items del técnico.

      setServiceOrderItems((prevValue: ServiceOrderItem[]) => {
        const index = prevValue.findIndex(soItem =>
          typeof soItem.package_item === 'number'
            ? soItem.package_item === packageItemId
            : soItem.package_item?.id === packageItemId,
        );

        if (index < 0) {
          return prevValue;
        }

        const nextItems = [...prevValue];
        const nextItem = { ...nextItems[index] };

        updater(nextItem);
        nextItem.toUpdate = true;
        nextItems[index] = nextItem;

        return nextItems;
      });
    },
    [setServiceOrderItems],
  );
  // Setteo de cantidades
  const handleUsedAmountChange = useCallback(
    (item: PackageItem, value: number | null) => {
      if (!item.id) {
        return;
      }

      const maxAmount = Math.max(item.execute_amount ?? 0, 0);
      const nextAmount = typeof value === 'number' ? Math.min(Math.max(value, 0), maxAmount) : 0;

      updateServiceOrderItem(item.id, nextItem => {
        nextItem.used_amount = nextAmount;
      });
    },
    [updateServiceOrderItem],
  );

  return (
    <Table<PackageItem>
      className="ServiceOrderShippedItemsPackageTable"
      tableLayout="fixed"
      rowKey={record => record.id ?? `package-item-${packageGroup.packageId ?? 'x'}`}
      dataSource={packageGroup.items}
      pagination={false}
      size="small"
      scroll={{ x: 900 }}>
      <Table.Column
        width={220}
        title={t('Elemento')}
        render={(item: PackageItem) => item.name ?? '-'}
      />
      <Table.Column
        width={140}
        title={t('Número de serie')}
        render={(item: PackageItem) => item.serial_number ?? '-'}
      />
      <Table.Column
        width={140}
        title={t('Cant. enviada')}
        render={(item: PackageItem) => item.execute_amount ?? 0}
      />
      <Table.Column
        width={130}
        title={
          <Tooltip title={t('Cantidad utilizada en el servicio realizado')}>
            {t('Cantidad utilizada')}
            <QuestionCircleOutlined style={{ marginLeft: '4px', marginTop: '4px' }} />
          </Tooltip>
        }
        render={(item: PackageItem) => {
          const row = getRowState(item);

          return (
            <Tooltip
              title={
                row.isNoReturnOrSale
                  ? 'Los items de venta de repuesto o sin devolución se consideran utilizados en el servicio'
                  : ''
              }>
              <FormattedInputNumber
                min={0}
                max={row.maxAmount}
                disabled={!row.selected || row.maxAmount <= 0 || row.isNoReturnOrSale}
                value={row.selected?.used_amount}
                onChange={value => handleUsedAmountChange(item, value)}
                style={{ width: '100%' }}
              />
            </Tooltip>
          );
        }}
      />
      <Table.Column
        width={130}
        title={
          <Tooltip title={t('Cantidad que el técnico mantendra en su stock personal')}>
            {t('Cantidad para stock técnico')}
            <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
          </Tooltip>
        }
        render={(item: PackageItem) => {
          const row = getRowState(item);

          return (
            <Tooltip title={row.isNoReturnOrSale ? 'No corresponde para este ítem' : ''}>
              <FormattedInputNumber
                min={0}
                max={row.maxStockAllowed}
                disabled={!row.selected || row.maxAmount <= 0 || row.isNoReturnOrSale}
                value={row.selected?.stock_technician_amount}
                onChange={value =>
                  item?.id &&
                  updateServiceOrderItem(item.id, nextItem => {
                    nextItem.stock_technician_amount = value ?? undefined;
                  })
                }
                style={{ width: '100%' }}
                status={row.isInvalid ? 'error' : undefined}
              />
            </Tooltip>
          );
        }}
      />
      <Table.Column
        width={130}
        title={
          <Tooltip title={t('Cantidad del producto retirado del cliente que se decidió desechar')}>
            {t('Cantidad descartada')}
            <QuestionCircleOutlined style={{ marginLeft: '4px', marginTop: '4px' }} />
          </Tooltip>
        }
        render={(item: PackageItem) => {
          const row = getRowState(item);

          return (
            <Tooltip title={row.isNoReturnOrSale ? 'No corresponde para este ítem' : ''}>
              <FormattedInputNumber
                min={0}
                max={row.maxDiscardedAllowed}
                disabled={!row.selected || row.maxAmount <= 0 || row.isNoReturnOrSale}
                value={row.selected?.discarded_amount}
                onChange={value =>
                  item?.id &&
                  updateServiceOrderItem(item.id, nextItem => {
                    nextItem.discarded_amount = value ?? undefined;
                  })
                }
                style={{ width: '100%' }}
                status={row.isInvalid ? 'error' : undefined}
              />
            </Tooltip>
          );
        }}
      />
      <Table.Column
        width={130}
        title={
          <Tooltip
            title={t(
              'Cantidad del producto que será devuelto a Acronex, ya sea utilizado por el cliente o enviado preventivamente',
            )}>
            {t('Cantidad a devolver')}
            <QuestionCircleOutlined style={{ marginLeft: '4px', marginTop: '4px' }} />
          </Tooltip>
        }
        render={(item: PackageItem) => {
          const row = getRowState(item);

          return (
            <Tooltip title={row.isNoReturnOrSale ? 'No corresponde para este ítem' : ''}>
              <FormattedInputNumber
                min={0}
                max={row.maxReturnAllowed}
                disabled={!row.selected || row.maxAmount <= 0 || row.isNoReturnOrSale}
                value={row.selected?.to_return_amount}
                onChange={value =>
                  item?.id &&
                  updateServiceOrderItem(item.id, nextItem => {
                    nextItem.to_return_amount = value ?? undefined;
                  })
                }
                style={{ width: '100%' }}
                status={row.isInvalid ? 'error' : undefined}
              />
            </Tooltip>
          );
        }}
      />

      <Table.Column
        title={t('Observaciones')}
        width={210}
        render={(_: any, item: PackageItem) => {
          return (
            <Flex gap="4px">
              {item.in_warranty_period && (
                <Tag className="serviceOrderLoadedItemsTag" color="green">
                  {t('En garantía')}
                </Tag>
              )}
              {item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE && (
                <Tag className="serviceOrderLoadedItemsTag" color="orange">
                  {t('Sujeto a peritaje')}
                </Tag>
              )}
              {item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.PREVENTIVO && (
                <Tag className="serviceOrderLoadedItemsTag" color="orange">
                  {t('Preventivo')}
                </Tag>
              )}
              {item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO && (
                <Tag className="serviceOrderLoadedItemsTag" color="blue">
                  {t('Venta de repuesto')}
                </Tag>
              )}
              {item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION && (
                <Tag className="serviceOrderLoadedItemsTag" color="blue">
                  {t('Sin devolución')}
                </Tag>
              )}
            </Flex>
          );
        }}
      />
    </Table>
  );
};

export default ServiceOrderShippedItemsTable;
